import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { summaryReportsService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  FileText, Plus, Edit3, Eye, CheckCircle2, 
  Loader2, AlertCircle, ExternalLink, UtensilsCrossed, Wine, Trash2, EyeOff
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getApiError } from '@/lib/utils';

/**
 * SummaryReportSection - Phase 2A + Scope Update
 * 
 * Shows Summary Stock Audit Report actions within the Job Detail Reports tab.
 * Now supports per-scope reports:
 * - Food-only jobs: Shows 1 card (FOOD scope)
 * - Beverage-only jobs: Shows 1 card (BEVERAGE scope)
 * - Food & Beverages jobs: Shows 2 cards (FOOD and BEVERAGE scopes)
 * - Valuation-only jobs: Shows nothing (not eligible)
 */
export default function SummaryReportSection({ jobId, job }) {
  const navigate = useNavigate();
  const { isAdmin, isScheduler, isAuditor } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [existingReports, setExistingReports] = useState([]);
  const [error, setError] = useState(null);
  const [deleteReportId, setDeleteReportId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [unpublishReportId, setUnpublishReportId] = useState(null);
  const [unpublishing, setUnpublishing] = useState(false);
  
  // Check if user can delete (Admin or Scheduler)
  const canDelete = isAdmin || isScheduler;
  
  // Check job eligibility for Summary Report
  const checkEligibility = useCallback(async () => {
    if (!jobId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await summaryReportsService.checkEligibility(jobId);
      setEligibility(data);
      
      // Set existing reports from eligibility response
      setExistingReports(data.existing_reports || []);
    } catch (err) {
      console.error('[SummaryReport] Failed to check eligibility:', err);
      setError(getApiError(err, 'Failed to check eligibility'));
    } finally {
      setLoading(false);
    }
  }, [jobId]);
  
  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);
  
  // Handle navigation to Summary Report page
  const handleNavigateToReport = (scope, reportId = null) => {
    if (reportId) {
      // Navigate to existing report
      navigate(`/summary-reports/${reportId}`);
    } else {
      // Navigate to create new report with scope
      navigate(`/jobs/${jobId}/summary-report?scope=${scope}`);
    }
  };
  
  // Handle delete report
  const handleDeleteReport = async () => {
    if (!deleteReportId) return;
    
    setDeleting(true);
    try {
      await summaryReportsService.delete(deleteReportId);
      toast.success('Report deleted successfully');
      setDeleteReportId(null);
      checkEligibility();
    } catch (err) {
      console.error('[SummaryReport] Failed to delete report:', err);
      toast.error(getApiError(err, 'Failed to delete report'));
    } finally {
      setDeleting(false);
    }
  };
  
  // Handle unpublish report
  const handleUnpublishReport = async () => {
    if (!unpublishReportId) return;
    
    setUnpublishing(true);
    try {
      await summaryReportsService.unpublish(unpublishReportId);
      toast.success('Report unpublished — reverted to Draft');
      setUnpublishReportId(null);
      checkEligibility();
    } catch (err) {
      console.error('[SummaryReport] Failed to unpublish report:', err);
      toast.error(getApiError(err, 'Failed to unpublish report'));
    } finally {
      setUnpublishing(false);
    }
  };
  
  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr), 'd MMM yyyy');
  };
  
  // Get icon for scope
  const getScopeIcon = (scope) => {
    if (scope === 'FOOD') {
      return <UtensilsCrossed className="h-5 w-5" />;
    }
    if (scope === 'SHOP') {
      return <UtensilsCrossed className="h-5 w-5" />;
    }
    return <Wine className="h-5 w-5" />;
  };
  
  // Get existing report for a scope
  const getReportForScope = (scope) => {
    return existingReports.find(r => r.scope === scope);
  };
  
  // If loading, show spinner
  if (loading) {
    return (
      <Card className="mb-4">
        <CardContent className="py-6">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Checking eligibility...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // If error, show error message
  if (error) {
    return (
      <Card className="mb-4">
        <CardContent className="py-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }
  
  // If not eligible, don't show anything
  if (!eligibility?.is_eligible || !eligibility?.eligible_scopes?.length) {
    return null;
  }
  
  // Check access - Auditor, Scheduler, or Admin can create/view
  const canAccess = isAdmin || isScheduler || isAuditor;
  
  if (!canAccess) {
    return null;
  }
  
  const isMandatory = eligibility.is_mandatory;
  const eligibleScopes = eligibility.eligible_scopes || [];
  
  // Render a single scope card
  const renderScopeCard = (scope) => {
    const existingReport = getReportForScope(scope);
    const hasReport = !!existingReport;
    const isPublished = existingReport?.status === 'PUBLISHED';
    const scopeLabel = scope === 'FOOD' ? 'Food' : scope === 'SHOP' ? 'Shop' : 'Beverage';
    const scopeDescription = scope === 'FOOD' 
      ? 'Food Stock Audit summary for this job'
      : scope === 'SHOP'
      ? 'Shop Stock Audit summary for this job'
      : 'Beverage Stock Audit summary for this job';
    
    // Determine if this scope still needs a report (for mandatory badge)
    const needsReport = isMandatory && !hasReport;
    
    return (
      <Card 
        key={scope}
        className={`mb-4 ${needsReport ? 'border-amber-400 bg-amber-50/30' : 'border-blue-200 bg-blue-50/30'}`} 
        data-testid={`summary-report-section-${scope.toLowerCase()}`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={needsReport ? 'text-amber-600' : 'text-blue-600'}>
                {getScopeIcon(scope)}
              </span>
              <CardTitle className="text-base">{scopeLabel} Summary Report</CardTitle>
              {/* Mandatory flag */}
              {needsReport && (
                <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-300">
                  Required
                </Badge>
              )}
            </div>
            {hasReport && (
              <Badge 
                className={`text-xs ${
                  isPublished 
                    ? 'bg-green-100 text-green-800 border-green-300' 
                    : 'bg-amber-100 text-amber-800 border-amber-300'
                }`}
              >
                {isPublished ? (
                  <>
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Published
                  </>
                ) : (
                  <>
                    <Edit3 className="h-3 w-3 mr-1" />
                    Draft
                  </>
                )}
              </Badge>
            )}
          </div>
          <CardDescription className="text-sm">
            {scopeDescription}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {hasReport 
                ? (isPublished ? 'Published' : 'Draft - In Progress')
                : 'No report created yet'
              }
            </p>
            
            <div className="flex items-center gap-2">
              {/* Unpublish button for Admin/Scheduler (only for PUBLISHED) */}
              {hasReport && isPublished && canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setUnpublishReportId(existingReport.report_id);
                  }}
                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  data-testid={`unpublish-report-btn-${scope.toLowerCase()}`}
                >
                  <EyeOff className="h-4 w-4 mr-1" />
                  Unpublish
                </Button>
              )}
              
              {/* Delete button for Admin/Scheduler (only for DRAFT) */}
              {hasReport && !isPublished && canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteReportId(existingReport.report_id);
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-report-btn-${scope.toLowerCase()}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              
              <Button 
                onClick={() => handleNavigateToReport(scope, existingReport?.report_id)}
                variant={hasReport ? "outline" : "default"}
                size="sm"
                data-testid={`summary-report-action-btn-${scope.toLowerCase()}`}
              >
                {hasReport ? (
                  <>
                    {isPublished ? (
                      <>
                        <Eye className="h-4 w-4 mr-2" />
                        View Report
                      </>
                    ) : (
                      <>
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit Report
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Report
                  </>
                )}
                <ExternalLink className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div data-testid="summary-report-section">
      {/* Header when multiple scopes */}
      {eligibleScopes.length > 1 && (
        <div className="mb-3 flex items-center gap-2">
          <FileText className="h-5 w-5 text-slate-600" />
          <h3 className="text-sm font-medium text-slate-600">
            Summary Stock Audit Reports
          </h3>
          <span className="text-xs text-slate-500">
            (This job has {eligibleScopes.length} eligible scopes)
          </span>
        </div>
      )}
      
      {/* Render a card for each eligible scope */}
      {eligibleScopes.map(scope => renderScopeCard(scope))}
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteReportId} onOpenChange={(open) => !open && setDeleteReportId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Summary Report?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The report will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteReport} 
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Unpublish Confirmation Dialog */}
      <AlertDialog open={!!unpublishReportId} onOpenChange={(open) => !open && setUnpublishReportId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish Summary Report?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revert the report to Draft status. It will no longer be visible to client portal users until re-published.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unpublishing}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleUnpublishReport} 
              disabled={unpublishing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {unpublishing ? 'Unpublishing...' : 'Unpublish'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
